import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Upload, Check, Heart, Share2, Loader2 } from 'lucide-react';
import { toPng } from 'html-to-image';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DRAFT_KEY = 'manasmc:register-draft:v1';

const defaultFormState = (matricula) => ({
  email: '',
  senha: '',
  confirmar_senha: '',
  nome_completo: '',
  nome_guerra: '',
  cpf: '',
  rg: '',
  endereco: '',
  telefone: '',
  tipo_sanguineo: '',
  data_nascimento: '',
  como_conheceu: matricula ? 'indicacao' : '',
  nome_madrinha: '',
  tamanho_camiseta: 'M',
  tipo_camiseta: 'Baby Look',
  tipo_membro: 'integrante',
  tem_moto: true,
  modelo_moto: '',
  placa_moto: '',
  chave_pix: '',
  aceite_estatuto: false,
  aceite_rdi: false,
  aceite_regulamento: false,  // sinônimo legado, mantido para backend
});

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { matriculaParam, slug: tenantSlugParam } = useParams();
  const [searchParams] = useSearchParams();
  const matricula = matriculaParam || searchParams.get('madrinha');
  const tenantSlug = tenantSlugParam || searchParams.get('mc');
  
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved?.step && saved.step >= 1 && saved.step <= 3) return saved.step;
      }
    } catch {/* ignore */}
    return 1;
  });
  const [madrinhaInfo, setMadrinhaInfo] = useState(null);
  const [fotoPerfil, setFotoPerfil] = useState(null);
  const [fotoCnh, setFotoCnh] = useState(null);
  const [fotoPerfilPreview, setFotoPerfilPreview] = useState(null);
  const [fotoCnhPreview, setFotoCnhPreview] = useState(null);
  const [restauradoDraft, setRestauradoDraft] = useState(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        return !!(saved?.data?.nome_completo || saved?.data?.email);
      }
    } catch {/* ignore */}
    return false;
  });
  const [formData, setFormData] = useState(() => {
    // Restaura rascunho salvo no localStorage, se existir
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === 'object' && saved.data) {
          return {
            ...defaultFormState(matricula),
            ...saved.data,
            // nunca restaura senhas (segurança)
            senha: '',
            confirmar_senha: '',
          };
        }
      }
    } catch {/* ignore */}
    return defaultFormState(matricula);
  });
  const [docs, setDocs] = useState({ estatuto: null, rdi: null });

  // Salva rascunho no localStorage a cada mudança (sem senhas)
  useEffect(() => {
    try {
      const { senha, confirmar_senha, ...safeData } = formData;
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        data: safeData,
        step,
        updated_at: new Date().toISOString(),
      }));
    } catch {/* ignore quota errors */}
  }, [formData, step]);

  const limparRascunho = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch {/* ignore */}
    setRestauradoDraft(false);
  };

  const descartarRascunho = () => {
    limparRascunho();
    setFormData(defaultFormState(matricula));
    setStep(1);
    toast.success('Rascunho apagado. Cadastro reiniciado.');
  };

  // Busca os 2 documentos ao montar (sem precisar login)
  useEffect(() => {
    axios.get(`${API_URL}/api/public/documentos-cadastro`)
      .then(r => setDocs(r.data))
      .catch(() => {});
  }, []);

  // Carregar informações da madrinha se tem matrícula
  useEffect(() => {
    if (matricula) {
      axios.get(`${API_URL}/api/referral/lookup/${matricula}`)
        .then(response => {
          setMadrinhaInfo(response.data);
          setFormData(prev => ({
            ...prev,
            nome_madrinha: response.data.nome,
            como_conheceu: 'indicacao'
          }));
          toast.success(`Madrinha ${response.data.nome} identificada! 💜`);
        })
        .catch(() => {
          toast.error('Link de indicação inválido');
        });
    }
  }, [matricula]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.senha !== formData.confirmar_senha) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    if (!formData.aceite_estatuto || !formData.aceite_rdi) {
      toast.error('Você precisa aceitar o Estatuto E o RDI para continuar');
      return;
    }
    
    setLoading(true);
    
    const { confirmar_senha, ...dataToSend } = formData;
    dataToSend.aceite_regulamento = true; // compat backend
    if (tenantSlug) dataToSend.tenant_slug = tenantSlug;
    const result = await register(dataToSend);
    
    if (result.success) {
      // Upload fotos (opcional, mas se enviar usa o token recém-gerado)
      try {
        const token = result.token || (result.user && result.user.token);
        if (fotoPerfil && token) {
          const fd = new FormData();
          fd.append('file', fotoPerfil);
          await axios.post(`${API_URL}/api/upload/profile-photo`, fd, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
          });
        }
        if (fotoCnh && token) {
          const fd = new FormData();
          fd.append('file', fotoCnh);
          await axios.post(`${API_URL}/api/upload/cnh`, fd, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
          });
        }
      } catch (err) {
        toast.warning('Cadastro criado, mas houve erro ao enviar as fotos. Envie pelo perfil.');
      }
      toast.success(result.message || 'Cadastro realizado com sucesso! 💜');
      limparRascunho();
      // Redireciona pra pagamento da inscrição (R$100 - taxa + camiseta)
      navigate('/pagar-inscricao');
    } else {
      toast.error(result.error || 'Erro ao realizar cadastro');
    }
    
    setLoading(false);
  };

  const handleFotoPerfil = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Foto muito grande (máx 10MB)'); return; }
    setFotoPerfil(file);
    setFotoPerfilPreview(URL.createObjectURL(file));
  };

  const handleFotoCnh = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Arquivo muito grande (máx 10MB)'); return; }
    setFotoCnh(file);
    setFotoCnhPreview(URL.createObjectURL(file));
  };

  const nextStep = () => {
    if (step === 1) {
      if (!formData.email || !formData.senha || !formData.confirmar_senha) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }
      if (formData.senha !== formData.confirmar_senha) {
        toast.error('As senhas não coincidem');
        return;
      }
    }
    if (step === 2) {
      const faltando = [];
      if (!formData.nome_completo?.trim()) faltando.push('Nome completo');
      if (!formData.cpf?.trim()) faltando.push('CPF');
      if (!formData.rg?.trim()) faltando.push('RG');
      if (!formData.endereco?.trim()) faltando.push('Endereço');
      if (!formData.telefone?.trim()) faltando.push('Telefone');
      if (!formData.como_conheceu?.trim()) faltando.push('Como conheceu');
      if (faltando.length) {
        toast.error('Faltam preencher: ' + faltando.join(', '), { duration: 6000 });
        return;
      }
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_manas-ecosystem/artifacts/6apyvsc0_WhatsApp%20Image%202026-04-18%20at%2011.52.59%20%283%29.jpeg" 
            alt="Giraia Bike Shop" 
            className="w-24 h-24 mx-auto mb-4 object-contain"
          />
          <h1 className="text-4xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Cadastro de Integrante
          </h1>
          <p className="text-[#A19BAA]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Preencha os dados para fazer parte das Giraia Bike Shop
          </p>
        </div>

        {/* Madrinha Banner */}
        {madrinhaInfo && (
          <div className="mb-6 bg-gradient-to-r from-pink-500/20 to-[#E2B84A]/20 border-2 border-pink-500/50 rounded-xl p-4" data-testid="madrinha-banner">
            <div className="flex items-center gap-3">
              <div className="bg-pink-500/30 rounded-full p-2">
                <Heart className="w-6 h-6 text-pink-300" />
              </div>
              <div>
                <p className="text-pink-200 text-sm font-bold">Indicação de Madrinha 💜</p>
                <p className="text-white font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {madrinhaInfo.nome} · {madrinhaInfo.divisao}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Rascunho Restaurado Banner */}
        {restauradoDraft && (
          <div className="mb-6 bg-[#FFD700]/10 border border-[#FFD700]/50 rounded-xl p-4 flex items-start gap-3" data-testid="draft-banner">
            <div className="text-2xl">💾</div>
            <div className="flex-1">
              <p className="text-[#FFD700] font-bold text-sm">Rascunho restaurado</p>
              <p className="text-[#E7E3EC] text-xs mt-1">
                Retomamos seu cadastro de onde você parou (senhas não ficam salvas, preencha de novo). Toque em "Descartar" para começar do zero.
              </p>
            </div>
            <button
              type="button"
              onClick={descartarRascunho}
              data-testid="descartar-rascunho-button"
              className="text-[#FFD700] hover:text-white text-xs font-bold underline shrink-0"
            >
              Descartar
            </button>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <StepIndicator active={step >= 1} completed={step > 1} number="1" label="Dados de Acesso" />
            <div className={`flex-1 h-1 mx-2 ${step > 1 ? 'bg-[#E2B84A]' : 'bg-[#2D243F]'}`}></div>
            <StepIndicator active={step >= 2} completed={step > 2} number="2" label="Dados Pessoais" />
            <div className={`flex-1 h-1 mx-2 ${step > 2 ? 'bg-[#E2B84A]' : 'bg-[#2D243F]'}`}></div>
            <StepIndicator active={step >= 3} completed={step > 3} number="3" label="Moto, Fotos & PIX" />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#120E1A] border border-[#2D243F] rounded-xl p-8" data-testid="register-form">
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Dados de Acesso
              </h2>
              
              <div>
                <label className="block text-white font-medium mb-2">Email *</label>
                <input
                  type="email"
                  required
                  data-testid="register-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                  placeholder="seu@email.com"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Senha *</label>
                <input
                  type="password"
                  required
                  data-testid="register-password-input"
                  value={formData.senha}
                  onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Confirmar Senha *</label>
                <input
                  type="password"
                  required
                  data-testid="register-confirm-password-input"
                  value={formData.confirmar_senha}
                  onChange={(e) => setFormData({ ...formData, confirmar_senha: e.target.value })}
                  className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                  placeholder="Digite a senha novamente"
                />
              </div>

              <button
                type="button"
                onClick={nextStep}
                data-testid="register-next-step-1"
                className="w-full bg-[#E2B84A] text-white font-bold rounded-lg px-6 py-3 hover:bg-[#F0D98C] transition-colors"
              >
                Próximo
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Dados Pessoais
              </h2>

              <div>
                <label className="block text-white font-medium mb-2">Nome Completo *</label>
                <input
                  type="text"
                  required
                  data-testid="register-name-input"
                  value={formData.nome_completo}
                  onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
                  className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">
                  Nome de Guerra <span className="text-[#716A80] text-sm">(nome que vai no colete)</span>
                </label>
                <input
                  type="text"
                  data-testid="register-guerra-input"
                  value={formData.nome_guerra}
                  onChange={(e) => setFormData({ ...formData, nome_guerra: e.target.value.toUpperCase() })}
                  className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none uppercase"
                  placeholder="Ex: LOBINHA, FLECHA, GUERREIRA"
                  maxLength="20"
                />
                <p className="text-xs text-[#716A80] mt-1">
                  Apelido curto que aparecerá nas costas do colete e na carteirinha
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">CPF *</label>
                  <input
                    type="text"
                    required
                    data-testid="register-cpf-input"
                    value={formData.cpf}
                    onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                    className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                    placeholder="000.000.000-00"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">RG *</label>
                  <input
                    type="text"
                    required
                    data-testid="register-rg-input"
                    value={formData.rg}
                    onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
                    className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Endereço *</label>
                <input
                  type="text"
                  required
                  data-testid="register-address-input"
                  value={formData.endereco}
                  onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                  className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Telefone *</label>
                  <input
                    type="tel"
                    required
                    data-testid="register-phone-input"
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Tipo Sanguíneo</label>
                  <select
                    data-testid="register-blood-type-select"
                    value={formData.tipo_sanguineo}
                    onChange={(e) => setFormData({ ...formData, tipo_sanguineo: e.target.value })}
                    className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                  >
                    <option value="">Selecione</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">
                  🎂 Data de Nascimento <span className="text-[#716A80] text-sm">(usada nos flyers de aniversário)</span>
                </label>
                <input
                  type="date"
                  data-testid="register-birthdate-input"
                  value={formData.data_nascimento}
                  onChange={(e) => setFormData({ ...formData, data_nascimento: e.target.value })}
                  className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Como conheceu o Giraia Bike Shop? *</label>
                <select
                  required
                  data-testid="register-how-met-select"
                  value={formData.como_conheceu}
                  onChange={(e) => setFormData({ ...formData, como_conheceu: e.target.value })}
                  className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                >
                  <option value="">Selecione</option>
                  <option value="indicacao">Indicação de Integrante</option>
                  <option value="redes_sociais">Redes Sociais</option>
                  <option value="evento">Evento do MC</option>
                  <option value="amiga">Amiga/Conhecida</option>
                  <option value="outro">Outro</option>
                </select>
              </div>

              {formData.como_conheceu === 'indicacao' && (
                <div>
                  <label className="block text-white font-medium mb-2">Nome da Madrinha</label>
                  <input
                    type="text"
                    data-testid="register-godmother-input"
                    value={formData.nome_madrinha}
                    onChange={(e) => setFormData({ ...formData, nome_madrinha: e.target.value })}
                    className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Tamanho da Camiseta Grátis *</label>
                  <select
                    required
                    data-testid="register-shirt-size-select"
                    value={formData.tamanho_camiseta}
                    onChange={(e) => setFormData({ ...formData, tamanho_camiseta: e.target.value })}
                    className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                  >
                    <option value="PP">PP</option>
                    <option value="P">P</option>
                    <option value="M">M</option>
                    <option value="G">G</option>
                    <option value="GG">GG</option>
                    <option value="XG">XG</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Tipo de Camiseta *</label>
                  <select
                    required
                    data-testid="register-shirt-type-select"
                    value={formData.tipo_camiseta}
                    onChange={(e) => setFormData({ ...formData, tipo_camiseta: e.target.value })}
                    className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                  >
                    <option value="Baby Look">Baby Look</option>
                    <option value="Tradicional">Tradicional</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={prevStep}
                  data-testid="register-prev-step-2"
                  className="flex-1 bg-transparent border border-[#E5E5E5] text-[#E5E5E5] font-bold rounded-lg px-6 py-3 hover:bg-[#E5E5E5] hover:text-black transition-colors"
                >
                  Voltar
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  data-testid="register-next-step-2"
                  className="flex-1 bg-[#E2B84A] text-white font-bold rounded-lg px-6 py-3 hover:bg-[#F0D98C] transition-colors"
                >
                  Próximo
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Moto, Fotos & Financeiro
              </h2>

              {/* Tem Moto Toggle */}
              <div>
                <label className="block text-white font-medium mb-3">Você tem moto própria? *</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tem_moto: true })}
                    data-testid="tem-moto-sim-button"
                    className={`p-4 rounded-lg border-2 font-bold transition-all ${
                      formData.tem_moto === true
                        ? 'border-[#E2B84A] bg-[#E2B84A]/20 text-white'
                        : 'border-[#2D243F] bg-[#050505] text-[#A19BAA] hover:border-[#E2B84A]/50'
                    }`}
                  >
                    <div className="text-2xl mb-1">🏍️</div>
                    <div>Sim, tenho moto</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tem_moto: false, modelo_moto: '', placa_moto: '' })}
                    data-testid="tem-moto-nao-button"
                    className={`p-4 rounded-lg border-2 font-bold transition-all ${
                      formData.tem_moto === false
                        ? 'border-[#FFD700] bg-[#FFD700]/20 text-white'
                        : 'border-[#2D243F] bg-[#050505] text-[#A19BAA] hover:border-[#FFD700]/50'
                    }`}
                  >
                    <div className="text-2xl mb-1">🚫🏍️</div>
                    <div>Não tenho ainda</div>
                  </button>
                </div>
              </div>

              {/* Dados da Moto - apenas se tem moto */}
              {formData.tem_moto && (
                <div className="bg-[#050505] border border-[#E2B84A]/30 rounded-lg p-4 space-y-4">
                  <p className="text-[#E2B84A] font-bold text-sm">🏍️ Dados da Moto</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white font-medium mb-2">Modelo da Moto *</label>
                      <input
                        type="text"
                        required={formData.tem_moto}
                        data-testid="register-bike-model-input"
                        value={formData.modelo_moto}
                        onChange={(e) => setFormData({ ...formData, modelo_moto: e.target.value })}
                        className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                        placeholder="Ex: Honda CG 160"
                      />
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">Placa da Moto *</label>
                      <input
                        type="text"
                        required={formData.tem_moto}
                        data-testid="register-bike-plate-input"
                        value={formData.placa_moto}
                        onChange={(e) => setFormData({ ...formData, placa_moto: e.target.value.toUpperCase() })}
                        className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                        placeholder="ABC-1234"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Upload de Foto de Perfil */}
              <div className="bg-[#050505] border border-[#2D243F] rounded-lg p-4">
                <p className="text-white font-medium mb-3">📸 Foto de Perfil *</p>
                <div className="flex items-center gap-4">
                  {fotoPerfilPreview ? (
                    <img src={fotoPerfilPreview} alt="preview" className="w-20 h-20 rounded-full object-cover border-2 border-[#E2B84A]" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-[#1C1628] border-2 border-dashed border-[#2D243F] flex items-center justify-center text-[#716A80]">
                      <Upload size={24} />
                    </div>
                  )}
                  <label className="cursor-pointer bg-[#E2B84A] hover:bg-[#F0D98C] text-white font-bold rounded-lg px-4 py-2 text-sm transition-colors" data-testid="register-foto-label">
                    {fotoPerfil ? 'Trocar foto' : 'Escolher foto'}
                    <input type="file" accept="image/*" className="hidden" onChange={handleFotoPerfil} data-testid="register-foto-input" />
                  </label>
                </div>
                <p className="text-[#716A80] text-xs mt-2">Usada na carteirinha digital. Preferência por rosto bem visível.</p>
              </div>

              {/* Prévia da Carteirinha Digital */}
              <CarteirinhaPreview
                fotoPreview={fotoPerfilPreview}
                nomeCompleto={formData.nome_completo}
                nomeGuerra={formData.nome_guerra}
                divisao={tenantSlug ? 'Sua Divisão' : 'Sorriso · MT'}
                tipoSanguineo={formData.tipo_sanguineo}
                temMoto={formData.tem_moto}
              />

              {/* Upload da CNH */}
              <div className="bg-[#050505] border border-[#2D243F] rounded-lg p-4">
                <p className="text-white font-medium mb-3">🪪 Foto da CNH (frente)</p>
                <div className="flex items-center gap-4">
                  {fotoCnhPreview ? (
                    <img src={fotoCnhPreview} alt="preview" className="w-28 h-20 rounded-lg object-cover border-2 border-[#FFD700]" />
                  ) : (
                    <div className="w-28 h-20 rounded-lg bg-[#1C1628] border-2 border-dashed border-[#2D243F] flex items-center justify-center text-[#716A80]">
                      <Upload size={24} />
                    </div>
                  )}
                  <label className="cursor-pointer bg-[#FFD700] hover:bg-[#F4C430] text-black font-bold rounded-lg px-4 py-2 text-sm transition-colors" data-testid="register-cnh-label">
                    {fotoCnh ? 'Trocar CNH' : 'Escolher CNH'}
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFotoCnh} data-testid="register-cnh-input" />
                  </label>
                </div>
                <p className="text-[#716A80] text-xs mt-2">
                  {formData.tem_moto
                    ? 'Obrigatório para integrantes com moto. Pode enviar depois pelo perfil se preferir.'
                    : 'Opcional. Envie caso já tenha CNH.'}
                </p>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Chave PIX *</label>
                <input
                  type="text"
                  required
                  data-testid="register-pix-input"
                  value={formData.chave_pix}
                  onChange={(e) => setFormData({ ...formData, chave_pix: e.target.value })}
                  className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] focus:ring-1 focus:ring-[#E2B84A] outline-none"
                  placeholder="CPF, E-mail, Telefone ou Chave Aleatória"
                />
                <p className="text-sm text-[#716A80] mt-1">
                  Necessário para recebimento da Ajuda de Custo Operacional
                </p>
              </div>

              <div className="bg-[#1C1628] border border-[#FFD700]/30 rounded-lg p-6">
                <h3 className="text-lg font-bold text-[#FFD700] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Taxa de Inscrição: R$ 100,00
                </h3>
                <p className="text-[#A19BAA] text-sm mb-4">
                  Pagamento único para se tornar integrante oficial. Você será redirecionada para o pagamento após o cadastro.
                </p>
              </div>

              <div className="bg-[#050505] border border-[#FFD700]/30 rounded-lg p-4 space-y-4">
                <p className="text-[#FFD700] font-bold flex items-center gap-2">
                  📜 Documentos obrigatórios
                </p>
                
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    data-testid="register-aceite-estatuto"
                    checked={formData.aceite_estatuto}
                    onChange={(e) => setFormData({ ...formData, aceite_estatuto: e.target.checked })}
                    className="mt-1 w-5 h-5 accent-[#E2B84A] flex-shrink-0"
                  />
                  <span className="text-[#E7E3EC] text-sm">
                    Li e <strong className="text-[#FFD700]">ACEITO</strong> o{' '}
                    {docs.estatuto ? (
                      <a href={`${API_URL}${docs.estatuto.url}`} target="_blank" rel="noreferrer" className="text-[#E2B84A] font-bold underline" data-testid="link-estatuto">
                        Estatuto Social do Giraia Bike Shop
                      </a>
                    ) : (
                      <span className="text-[#E2B84A] font-bold">Estatuto Social do Giraia Bike Shop</span>
                    )}
                    , incluindo obrigações com mensalidade, hierarquia (Diretora, Sub-Diretora, Social, ADM) e cumprimento integral das regras associativas. *
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    data-testid="register-aceite-rdi"
                    checked={formData.aceite_rdi}
                    onChange={(e) => setFormData({ ...formData, aceite_rdi: e.target.checked })}
                    className="mt-1 w-5 h-5 accent-[#FFD700] flex-shrink-0"
                  />
                  <span className="text-[#E7E3EC] text-sm">
                    Li e <strong className="text-[#FFD700]">ACEITO</strong> o{' '}
                    {docs.rdi ? (
                      <a href={`${API_URL}${docs.rdi.url}`} target="_blank" rel="noreferrer" className="text-[#FFD700] font-bold underline" data-testid="link-rdi">
                        Regulamento Disciplinar Interno (RDI)
                      </a>
                    ) : (
                      <span className="text-[#FFD700] font-bold">Regulamento Disciplinar Interno (RDI)</span>
                    )}
                    , estando ciente das infrações (leves, médias, graves), sanções (repreensão, advertência, suspensão/gancho, rebaixamento, perda do colete/expulsão) e do processo disciplinar. *
                  </span>
                </label>
                
                <p className="text-[#716A80] text-xs italic">
                  Clique nos links acima para abrir os PDFs oficiais. Seu aceite fica registrado com data/hora.
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={prevStep}
                  data-testid="register-prev-step-3"
                  className="flex-1 bg-transparent border border-[#E5E5E5] text-[#E5E5E5] font-bold rounded-lg px-6 py-3 hover:bg-[#E5E5E5] hover:text-black transition-colors"
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit-button"
                  className="flex-1 bg-[#FFD700] text-black font-bold rounded-lg px-6 py-3 hover:bg-[#F4C430] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Finalizando...' : 'Finalizar Cadastro'}
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-[#A19BAA]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Já tem uma conta?{' '}
              <Link to="/login" className="text-[#E2B84A] hover:text-[#F0D98C] font-semibold" data-testid="register-login-link">
                Faça login
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

const StepIndicator = ({ active, completed, number, label }) => {
  return (
    <div className="flex flex-col items-center">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm mb-2 transition-all ${
        completed 
          ? 'bg-[#E2B84A] text-white' 
          : active 
            ? 'bg-[#E2B84A] text-white' 
            : 'bg-[#2D243F] text-[#716A80]'
      }`}>
        {completed ? <Check size={20} /> : number}
      </div>
      <span className={`text-xs ${active ? 'text-white' : 'text-[#716A80]'} text-center`}>
        {label}
      </span>
    </div>
  );
};

const CarteirinhaPreview = ({ fotoPreview, nomeCompleto, nomeGuerra, divisao, tipoSanguineo, temMoto }) => {
  const nome = (nomeCompleto || 'Sua Mana').trim();
  const guerra = (nomeGuerra || '').trim().toUpperCase();
  const cardRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  const compartilhar = async () => {
    if (!cardRef.current) return;
    setSharing(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#050505',
      });
      const blob = await (await fetch(dataUrl)).blob();
      const filename = `giraia-bike-${(guerra || 'mana').toLowerCase()}.png`;
      const file = new File([blob], filename, { type: 'image/png' });
      const shareText = `Estou virando MANA MC! 💜\nUm motoclube feminino que é irmandade de verdade. 🏍️✨\n#MANASMC #IRMANDADE`;

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: 'Giraia Bike Shop', text: shareText });
          toast.success('Prévia compartilhada! 💜');
          return;
        } catch (err) {
          if (err?.name === 'AbortError') return; // usuário cancelou
        }
      }

      // Fallback: download tradicional
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 1000);
      toast.success('Prévia salva! Compartilhe no WhatsApp/Instagram 📱');
    } catch (err) {
      console.error('[carteirinha share] erro', err);
      toast.error('Não foi possível gerar a imagem. Tente novamente.');
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#E2B84A]/10 via-[#120E1A] to-[#FFD700]/10 border border-[#FFD700]/40 rounded-xl p-5" data-testid="carteirinha-preview">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[#FFD700] font-bold text-sm flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          ✨ Prévia da sua carteirinha
        </p>
        <span className="text-[10px] bg-yellow-500/20 text-yellow-300 border border-yellow-500/40 rounded-full px-2 py-0.5 font-bold">
          PENDENTE
        </span>
      </div>

      <div
        ref={cardRef}
        className="relative w-full aspect-[1.586/1] rounded-2xl overflow-hidden border-2 shadow-[0_10px_40px_rgba(255,215,0,0.15)]"
        style={{
          borderColor: '#FFD700',
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/24783ee2-7dd6-491c-ab37-38a190df7607/images/5152ea339118e2d22e694a5e069baf0698c672684fd1a8dc5081f3499d9c1be1.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 to-transparent z-10">
          <div className="p-4 sm:p-5 h-full flex flex-col justify-between">
            {/* Topo: foto + status */}
            <div className="flex items-start justify-between">
              {fotoPreview ? (
                <img
                  src={fotoPreview}
                  alt="Foto"
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-[#FFD700]"
                  crossOrigin="anonymous"
                />
              ) : (
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/5 border-2 border-dashed border-[#FFD700]/40 flex items-center justify-center">
                  <Upload className="w-5 h-5 text-[#FFD700]/60" />
                </div>
              )}
              <span className="bg-yellow-500 text-black text-[10px] sm:text-xs font-black rounded-full px-2 py-0.5">
                PENDENTE
              </span>
            </div>

            {/* Conteúdo */}
            <div className="backdrop-blur-md bg-white/5 border border-white/10 rounded-lg sm:rounded-xl p-2 sm:p-3">
              {guerra && (
                <div className="mb-2 pb-2 border-b border-white/10">
                  <p className="text-[10px] text-[#A19BAA] mb-0.5">Nome de Guerra</p>
                  <p className="text-[#FFD700] font-black text-base sm:text-xl tracking-wider truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {guerra}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                <div>
                  <p className="text-[9px] sm:text-[10px] text-[#A19BAA]">Nome</p>
                  <p className="text-white font-bold text-xs sm:text-sm truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {nome}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] sm:text-[10px] text-[#A19BAA]">Matrícula</p>
                  <p className="text-white/80 font-mono font-bold text-xs sm:text-sm">— · — · —</p>
                </div>
                <div>
                  <p className="text-[9px] sm:text-[10px] text-[#A19BAA]">Divisão</p>
                  <p className="text-white font-bold text-xs sm:text-sm truncate">{divisao}</p>
                </div>
                {tipoSanguineo && (
                  <div>
                    <p className="text-[9px] sm:text-[10px] text-[#A19BAA]">Tipo Sanguíneo</p>
                    <p className="text-white font-bold text-xs sm:text-sm">{tipoSanguineo}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <p className="text-[9px] sm:text-[10px] text-[#A19BAA]">Categoria</p>
                  <p className="text-white font-bold text-xs sm:text-sm">
                    {temMoto === false ? '💜 Sem moto' : '🏍️ Com moto'}
                  </p>
                </div>
              </div>
            </div>

            {/* Rodapé fictício do QR */}
            <div className="flex justify-end">
              <div className="bg-white/30 backdrop-blur-sm border border-white/40 rounded-lg sm:rounded-xl p-2 text-[9px] sm:text-[10px] text-black/70 font-bold">
                QR após aprovação
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={compartilhar}
        disabled={sharing}
        data-testid="carteirinha-share-button"
        className="mt-4 w-full bg-gradient-to-r from-[#E2B84A] to-[#FFD700] hover:opacity-90 text-white font-black rounded-lg px-6 py-3 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {sharing ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Gerando imagem...</>
        ) : (
          <><Share2 className="w-4 h-4" /> Compartilhar prévia</>
        )}
      </button>

      <p className="text-[#A19BAA] text-xs mt-3 leading-relaxed">
        🔒 Esta é só uma prévia. Sua carteirinha oficial — com matrícula, QR Code e status <strong className="text-[#FFD700]">ATIVA</strong> — é gerada após a aprovação da diretoria.
        <br />
        <span className="text-[#FFD700]">💜 Convide as amigas:</span> compartilhe no WhatsApp ou Instagram!
      </p>
    </div>
  );
};

export default RegisterPage;
